import React, { useMemo } from "react";
import {
  Menu,
  menuClasses,
  MenuItem,
  Sidebar,
  sidebarClasses,
} from "react-pro-sidebar";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import { Box, Stack, Typography } from "@mui/material";
import { UserRoles } from "src/constants/user";

import {
  AdminsSidebarIcon,
  CalendarSidebarIcon,
  EmployeesSidebarIcon,
  LogOutSidebarIcon,
  ServicesSidebarIcon,
  StudiosSidebarIcon,
  WorkingTimeSidebarIcon,
} from "../../Icons";
import { logout } from "../../store/reducers/authSlice";
import { closeDrawer } from "../../store/reducers/drawerSlice";
import { resetSelectedRow } from "../../store/reducers/tableSlice";

interface SideBarItemProps {
  title: string;
  to: string;
  icon: any;
  selected: string;
}

const Item = ({ title, to, icon, selected }: SideBarItemProps) => {
  return (
    <MenuItem
      active={selected === title}
      icon={icon}
      component={<Link to={to} />}
    >
      <Typography fontSize={16} fontWeight={500}>
        {title}
      </Typography>
    </MenuItem>
  );
};

const SidebarSectionHeader = ({children}: any) => {
  return (<Typography sx={{
    color: '#EDBACB',
    fontWeight: '600',
    fontSize: '16px',
    textTransform: 'uppercase',
    marginLeft: '16px',
    padding: '8px 0px 16px',
  }}>
    {children}
  </Typography>)
}

const SidebarSectionDelimiter = () => {
  return (<Box sx={{
    height: '1px',
    backgroundColor: '#363636',
    margin: '16px 24px 24px',
  }} />)
}

const menuRoutes = [
  {
    to: "/",
    title: "Admins",
    icon: <AdminsSidebarIcon />,
    roles: [UserRoles.SUPER_ADMIN],
  },
  {
    to: "/employees",
    title: "Employees",
    icon: <EmployeesSidebarIcon />,
    roles: [UserRoles.SUPER_ADMIN, UserRoles.ADMIN],
  },
  {
    to: "/studios",
    title: "Studios",
    icon: <StudiosSidebarIcon />,
    roles: [UserRoles.SUPER_ADMIN, UserRoles.ADMIN],
  },
  {
    to: "/bookings",
    title: "Bookings",
    icon: <CalendarSidebarIcon />,
    roles: [UserRoles.SUPER_ADMIN, UserRoles.ADMIN, UserRoles.MASTER],
  },
];

const templateRoutes = [
  {
    to: "/schedule-template",
    title: "Working time",
    icon: <WorkingTimeSidebarIcon />,
    roles: [UserRoles.SUPER_ADMIN, UserRoles.ADMIN],
  },
  {
    to: "/services",
    title: "Services",
    icon: <ServicesSidebarIcon />,
    roles: [UserRoles.SUPER_ADMIN, UserRoles.ADMIN],
  },
];

export const SideBar = () => {
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const { user } = useSelector((state: any) => state.auth);

  const selected = useMemo(() => {
    return menuRoutes.find((el) => el.to === pathname)?.title
      || templateRoutes.find((el) => el.to === pathname)?.title
      || menuRoutes[0].title;
  }, [pathname]);

  const renderRoutes = (routes: any) => {
    return routes.filter(({ roles }: any) =>
        // @ts-ignore
        roles.some((role) => role === UserRoles[user?.role])
      )
        .map(({ title, to, icon }: any) => {
          return (
            <Item
              key={title}
              title={title}
              to={to}
              icon={icon}
              selected={selected}
            />
          );
        })
  }

  const handleCloseDrawer = () => {
    dispatch(resetSelectedRow());
    dispatch(closeDrawer());
  };

  React.useEffect(() => {
    handleCloseDrawer();
  }, [pathname]);

  const handleLogout = () => {
    dispatch(logout());
  };

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
      }}
    >
      <Sidebar
        backgroundColor="#1E1E2D"
        width="248px"
        rootStyles={{
          [`.${sidebarClasses.container}`]: {
            display: "flex",
          },
          [`.${menuClasses.root}`]: {
            display: "flex",
            width: "100%",
          },
          [`.${menuClasses.root}>ul`]: {
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            flex: 1,
            padding: 0,
          },
        }}
      >
        <Box display="flex" flex={1} flexDirection="column">
          <Box
            display="flex"
            alignItems="center"
            sx={{
              borderBottom: "1px solid #363636",
              height: 80,
            }}
          >
            <Stack direction="row" alignItems="baseline">
              <Box
                position="relative"
                bottom={-4}
                sx={{
                  m: "0 14px 0 24px",
                }}
              >
                <img alt="Logo" width="36px" src="/images/logo.png" />
              </Box>
              <Typography color="#CDC6E5" fontWeight="bold">
                Mon Lis CRM
              </Typography>
            </Stack>
          </Box>

          <Box display="flex" flex={1}>
            <Menu
              rootStyles={{
                [`.${menuClasses.active}`]: {
                  backgroundColor: "#3A3A57",
                  color: "#D6D6DB",
                  borderRadius: 10,
                },
                [`.${menuClasses.menuItemRoot}`]: {
                  borderRadius: 10,
                  marginBottom: 8,
                },
                [`.${menuClasses.active}.${menuClasses.label} p`]: {
                  fontWeight: "500",
                },
                [`.${menuClasses.button}`]: {
                  padding: "0 16px",
                  color: "#828282",
                  height: 48,
                },
                [`.${menuClasses.label}`]: {
                  fontWeight: "500",
                },
                [`.${menuClasses.button}:hover`]: {
                  backgroundColor: "transparent",
                  color: "#D6D6DB",
                },
                [`.${menuClasses.icon}`]: {
                  width: 24,
                  height: 24,
                  minWidth: 24,
                  lineHeight: 24,
                  marginRight: 16,
                },
              }}
            >
              {/* LOGO AND MENU ICON */}
              <Box
                display="flex"
                flexDirection="column"
                sx={{
                  padding: "20px 16px 0",
                }}
              >
                <SidebarSectionHeader>{'Menu'}</SidebarSectionHeader>
                {renderRoutes(menuRoutes)}
                <SidebarSectionDelimiter/>
                {
                  user?.role !== 'MASTER' ? (
                    <SidebarSectionHeader>{'Templates'}</SidebarSectionHeader>
                  ) : null
                }
                {renderRoutes(templateRoutes)}
              </Box>
              <Box
                display="flex"
                flexDirection="column"
                sx={{
                  padding: "0 16px 32px",
                }}
              >
                <MenuItem onClick={handleLogout} icon={<LogOutSidebarIcon />}>
                  <Typography fontSize={16} fontWeight={500}>
                    Log out
                  </Typography>
                </MenuItem>
              </Box>
            </Menu>
          </Box>
        </Box>
      </Sidebar>
    </Box>
  );
};
